import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { ScheduleService } from '@services/schedules.service';
import { ConfirmationDialogService } from '@services/confirmation-dialog/confirmation-dialog.service';
import { DisplayTags } from '@app/shared/models/tag.interface';
import { Floor } from '@app/shared/models/floor.interface';
import {
  AtSameTime,
  OffsetAfterTimes,
  OffsetBeforeTimes,
  Schedule,
  SolarEventOptions,
  solarEventRequiredValidator
} from '@app/shared/models/schedule';
import { MatButton } from '@angular/material/button';
import { MatError, MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { MatOptgroup, MatOption } from '@angular/material/autocomplete';
import { MatSelect } from '@angular/material/select';
import { Rule } from '@app/shared/models/rule';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { NgClass } from '@angular/common';
import { ITimeOption } from '@app/shared/models/time-option.interface';
import { ScheduleRuleComponent } from '@app/schedules/schedule-rule/schedule-rule.component';
import { IScheduleDateForm } from '@app/shared/models/schedule-form.interface';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { NotificationBlockComponent, StatusClass } from '@components/notification-block/notification-block.component';
import { ConfirmDialogData } from '@components/dialogs/confirm/confirm.component';
import { Building } from '@app/shared/models/building.interface';
import { TimeUtils } from '@app/shared/utils/time.utils';
import { TimezoneUtils } from '@app/shared/utils/timezoneUtils';
import { Observable } from 'rxjs';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import * as SunCalc from 'suncalc';
import { add, sub } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { ToastService } from '@services/toast/toast.service';

@Component({
  selector: 'app-form-dated-schedule',
  standalone: true,
  imports: [
    FormsModule,
    MatButton,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
    MatSuffix,
    MatTooltip,
    NgClass,
    NotificationBlockComponent,
    ReactiveFormsModule,
    ScheduleRuleComponent,
    MatSlideToggleModule,
    MatError,
    MatOptgroup
  ],
  templateUrl: './form-dated-schedule.component.html',
  styleUrl: './form-dated-schedule.component.scss'
})
export class FormDatedScheduleComponent implements OnInit {
  constructor(
    private fb: FormBuilder,
    private scheduleService: ScheduleService,
    private dialog: ConfirmationDialogService,
    private toastService: ToastService
  ) {}

  @Input() schedule: Schedule;
  @Input() isNew: boolean;
  @Input() tags$: Observable<DisplayTags>;
  @Input() building: Building;

  @Output() onDeleteSchedule = new EventEmitter<number>();
  @Output() onSaveNewSchedule = new EventEmitter<number>();
  @Output() onClose = new EventEmitter<void>();
  @Output() onChange = new EventEmitter<Schedule>();
  @Output() initialized = new EventEmitter<void>();
  scheduleForm: FormGroup<IScheduleDateForm>;
  saveLabel: string;
  readonly floors: Floor[] = [];
  minSelectableStartDate: Date;
  maxSelectableStartDate: Date;
  minSelectableEndDate: Date;
  maxSelectableEndDate: Date;
  currentDate: Date;
  timeOptions: ITimeOption[];
  solarEventOptions = SolarEventOptions;
  StatusClass = StatusClass;
  startDateTime: Date;
  endDateTime: Date;
  defaultTimeOption: ITimeOption = { timeString: '00:00', hour: 0, minute: 0, disabled: false };
  offsetBeforeTimes = OffsetBeforeTimes;
  offsetAfterTimes = OffsetAfterTimes;
  atSameTime = AtSameTime;

  ngOnInit(): void {
    if (!this.schedule.id) {
      this.isNew = true;
    }
    this.saveLabel = 'Save';
    this.currentDate = TimeUtils.convertTimezone(
      new Date(),
      TimezoneUtils.mapUtcToKnownTimezone(this.building.timeZone)
    );
    this.minSelectableStartDate =
      this.isNew || this.schedule.startDate > this.currentDate ? this.currentDate : this.schedule.startDate;
    this.minSelectableEndDate =
      this.isNew || this.schedule.endDate > this.currentDate ? this.currentDate : this.schedule.endDate;
    this.timeOptions = this.produceTimeOptions(this.currentDate);
    if (!this.schedule.startDate) {
      this.schedule.startDate = new Date(this.currentDate.getTime());
      this.schedule.endDate = new Date(this.schedule.startDate.getTime());
    }
    this.initFormControls();
  }

  initFormControls(): void {
    this.setupDates();
    const allOffsetOptions = [this.atSameTime, ...this.offsetBeforeTimes, ...this.offsetAfterTimes];
    const selectedOffsetStart =
      allOffsetOptions.find(
        (opt) =>
          opt.time.value === this.schedule.startOffset?.value && opt.time.offset === this.schedule.startOffset?.offset
      ) || this.atSameTime;
    const selectedOffsetEnd =
      allOffsetOptions.find(
        (opt) =>
          opt.time.value === this.schedule.endOffset?.value && opt.time.offset === this.schedule.endOffset?.offset
      ) || this.atSameTime;
    this.scheduleForm = this.fb.group(
      {
        isStartSolarEvent: [!!this.schedule.startSolarEvent, Validators.required],
        isEndSolarEvent: [!!this.schedule.endSolarEvent, Validators.required],
        name: [this.schedule.name || '', Validators.required],
        startDate: new FormControl(new Date(this.schedule.startDate.getTime()), {
          validators: [Validators.required],
          updateOn: 'blur'
        }),
        startTime: [
          this.schedule.startSolarEvent
            ? this.defaultTimeOption
            : this.timeOptions.find((element) => this.schedule.startTime === element?.timeString),
          [Validators.required]
        ],
        endDate: new FormControl(new Date(this.schedule.endDate.getTime()), {
          validators: [Validators.required],
          updateOn: 'blur'
        }),
        endTime: [
          this.schedule.endSolarEvent
            ? this.defaultTimeOption
            : this.timeOptions.find((element) => this.schedule.endTime === element?.timeString),
          [Validators.required]
        ],
        startSolarEvent: [this.schedule.startSolarEvent || null],
        endSolarEvent: [this.schedule.endSolarEvent || null],
        offsetStartTime: [selectedOffsetStart.time || this.atSameTime.time],
        offsetEndTime: [selectedOffsetEnd.time]
      },
      { validators: solarEventRequiredValidator }
    );
    this.initialized.emit();
  }

  get startDate(): AbstractControl {
    return this.scheduleForm.get('startDate');
  }

  get endDate(): AbstractControl {
    return this.scheduleForm.get('endDate');
  }

  get endTime(): AbstractControl {
    return this.scheduleForm.get('endTime');
  }

  get startTime(): AbstractControl {
    return this.scheduleForm.get('startTime');
  }

  get offsetStartTime(): AbstractControl {
    return this.scheduleForm.get('offsetStartTime');
  }

  get offsetEndTime(): AbstractControl {
    return this.scheduleForm.get('offsetEndTime');
  }

  get startSolarEvent(): AbstractControl {
    return this.scheduleForm.get('startSolarEvent');
  }

  get endSolarEvent(): AbstractControl {
    return this.scheduleForm.get('endSolarEvent');
  }

  delete(): void {
    const data = new ConfirmDialogData(`Delete schedule ${this.schedule.name}?`, 'Delete Schedule');
    this.dialog.open(data).subscribe({
      next: (result) => {
        if (result && this.schedule.id != null) {
          this.scheduleService.deleteSchedule(this.schedule).subscribe({
            next: () => {
              this.onDeleteSchedule.emit(this.schedule.id);
            }
          });
        } else {
          return;
        }
      }
    });
  }

  close(): void {
    this.onClose.emit();
  }

  save(): void {
    if (this.scheduleForm.invalid) {
      return;
    }

    this.saveLabel = 'Saving...';

    this.schedule.name = this.scheduleForm.get('name').value;
    if (this.schedule.id != null) {
      this.scheduleService.updateSchedule(Schedule.toDto(this.schedule)).subscribe({
        next: (schedule) => {
          this.schedule = Schedule.clone(Schedule.fromDto(schedule));
          this.close();
          this.saveLabel = 'Save';
        },
        error: () => {
          this.toastService.error({ message: 'Error updating Schedules', dataCy: 'update-schedule-error-toast' });
        }
      });
    } else {
      this.scheduleService.saveSchedule(Schedule.toDto(this.schedule), this.building.id).subscribe({
        next: (schedule) => {
          this.schedule = Schedule.clone(schedule);
          this.onSaveNewSchedule.emit();
          this.saveLabel = 'Save';
        },
        error: () => {
          this.toastService.error({ message: 'Error saving Schedules', dataCy: 'save-schedule-error-toast' });
        }
      });
    }
  }

  addRule(): void {
    this.schedule.rules.push(new Rule());
  }

  removeRule(index: number): void {
    this.schedule.rules.splice(index, 1);
  }

  onChangeStartDate(): void {
    this.minSelectableEndDate = this.scheduleForm.value.startDate;
    if (this.scheduleForm.value.endDate < this.minSelectableEndDate) {
      this.endDate.setValue(this.scheduleForm.value.startDate);
      this.onChangeEndDate();
    }
    this.schedule.startDate = this.startDate.getRawValue();
    this.resetSolarEventControls(true);
  }

  onChangeEndDate(): void {
    this.maxSelectableStartDate = this.scheduleForm.value.endDate;
    this.schedule.endDate = this.endDate.getRawValue();
    this.resetSolarEventControls(false);
  }

  onChangeStartTime(): void {
    this.schedule.startTime = this.startTime.getRawValue().timeString;
    this.onChange.emit(this.schedule);
  }

  onChangeEndTime(): void {
    this.schedule.endTime = this.endTime.getRawValue().timeString;
    this.onChange.emit(this.schedule);
  }

  onStartSolarEventsToggle(): void {
    this.resetSolarEventControls(true);
  }

  onEndSolarEventsToggle(): void {
    this.resetSolarEventControls(false);
  }

  onChangeStartSolarEvent(): void {
    this.schedule.startSolarEvent = this.startSolarEvent.value;
    this.updateSolarEventTimes(this.schedule.startSolarEvent, this.schedule.startDate, true);
    this.offsetStartTime.reset(this.atSameTime.time);
    this.offsetEndTime.enable();
  }

  onChangeEndSolarEvent(): void {
    this.schedule.endSolarEvent = this.endSolarEvent.value;
    this.updateSolarEventTimes(this.schedule.endSolarEvent, this.schedule.endDate, false);
    this.offsetEndTime.reset(this.atSameTime.time);
    this.offsetEndTime.enable();
  }

  onChangeOffsetStartTime(): void {
    const offsetStartTime = this.offsetStartTime.value;
    this.updateOffsetTime(offsetStartTime.value, offsetStartTime.offset, this.startDateTime, true);
  }

  onChangeOffsetEndTime(): void {
    const offsetEndTime = this.offsetEndTime.value;
    this.updateOffsetTime(offsetEndTime.value, offsetEndTime.offset, this.endDateTime, false);
  }

  get validateRules(): boolean {
    if (!this.schedule?.rules?.length) {
      return false;
    }

    return this.schedule.rules.every((rule) => {
      return rule.command != null && rule.value != null;
    });
  }

  get validateDateAndTime(): boolean {
    const endDate = this.scheduleForm.value.endDate?.getTime();
    const startDate = this.scheduleForm.value.startDate?.getTime();
    const startTime = this.scheduleForm.value.isStartSolarEvent
      ? this.schedule.startTime
      : this.scheduleForm.value.startTime?.timeString;
    const endTime = this.scheduleForm.value.isEndSolarEvent
      ? this.schedule.endTime
      : this.scheduleForm.value.endTime?.timeString;

    return endDate > startDate || (endDate === startDate && endTime > startTime);
  }

  get validateStartSolarEventSelected(): boolean {
    if (this.scheduleForm.get('isStartSolarEvent').value) {
      return !!this.scheduleForm.get('startSolarEvent').value;
    } else {
      return true;
    }
  }

  get validateEndSolarEventSelected(): boolean {
    if (this.scheduleForm.get('isEndSolarEvent').value) {
      return !!this.endSolarEvent.value;
    } else {
      return true;
    }
  }

  get startControlDirty(): boolean {
    return this.scheduleForm.controls.startTime.dirty || this.scheduleForm.controls.startSolarEvent.dirty;
  }

  get endControlsDirty(): boolean {
    return this.scheduleForm.controls.endTime.dirty || this.scheduleForm.controls.endSolarEvent.dirty;
  }

  private produceTimeOptions(date: Date): ITimeOption[] {
    const timeOptions: ITimeOption[] = [];
    for (let hours = 0; hours < 24; hours += 1) {
      for (let minutes = 0; minutes < 60; minutes += 10) {
        timeOptions.push({
          timeString: [('0' + hours).slice(-2), ('0' + minutes).slice(-2)].join(':'),
          hour: hours,
          minute: minutes,
          disabled: false
        });
      }
    }
    timeOptions.push({
      timeString: '23:59',
      hour: 23,
      minute: 59,
      disabled: false
    });

    return timeOptions;
  }

  private updateSolarEventTimes(astronomicalTime: string, localDate: Date, isStart: boolean): void {
    const utcDate = fromZonedTime(localDate.setHours(0, 0, 0, 0), this.building.timeZone);
    const astroTimes = this.getSolarTimes(utcDate);
    const selectedTime = astronomicalTime === 'SUNRISE' ? astroTimes.sunrise : astroTimes.sunset;
    const selectedTimeInBuildingTZ = toZonedTime(selectedTime, this.building.timeZone);
    const timeString = selectedTimeInBuildingTZ.toTimeString().slice(0, 5);

    if (isStart) {
      this.startDateTime = selectedTimeInBuildingTZ;
      this.schedule.startTime = timeString;
    } else {
      this.endDateTime = selectedTimeInBuildingTZ;
      this.schedule.endTime = timeString;
    }

    this.onChange.emit(this.schedule);
  }

  private updateOffsetTime(offsetTime: string, toggleValue: string, baseDateTime: Date, isStart: boolean): void {
    const updatedTime = toggleValue
      ? toggleValue === 'minus'
        ? sub(baseDateTime, { minutes: +offsetTime })
        : add(baseDateTime, { minutes: +offsetTime })
      : baseDateTime;

    const timeString = updatedTime.toTimeString().slice(0, 5);

    if (isStart) {
      this.schedule.startTime = timeString;
      this.schedule.startOffset = { value: offsetTime, offset: toggleValue };
    } else {
      this.schedule.endTime = timeString;
      this.schedule.endOffset = { value: offsetTime, offset: toggleValue };
    }
    this.onChange.emit(this.schedule);
  }

  private getSolarTimes(date?: Date): { sunrise: Date; sunset: Date } {
    const nextActiveDate = add(date, { hours: 4 });
    const astroTimes = SunCalc.getTimes(nextActiveDate, this.building.latitude, this.building.longitude);
    return {
      sunrise: astroTimes.sunrise,
      sunset: astroTimes.sunset
    };
  }

  private resetSolarEventControls(isStart: boolean): void {
    this.scheduleForm.get(isStart ? 'startTime' : 'endTime').reset(this.timeOptions[0]);
    this.scheduleForm.get(isStart ? 'startSolarEvent' : 'endSolarEvent').reset(null);
    (isStart ? this.offsetStartTime : this.offsetEndTime).reset(this.atSameTime.time);

    if (isStart) {
      this.schedule.startTime = '00:00';
      this.schedule.startSolarEvent = null;
      this.schedule.startOffset = undefined;
    } else {
      this.schedule.endTime = '00:00';
      this.schedule.endSolarEvent = null;
      this.schedule.endOffset = undefined;
    }

    this.onChange.emit(this.schedule);
  }

  private setupDates() {
    const startDate = TimeUtils.convertTimezone(
      new Date(this.schedule.nextRunStartTime),
      TimezoneUtils.mapUtcToKnownTimezone(this.building.timeZone)
    );
    this.startDateTime = this.schedule.startOffset?.offset
      ? this.schedule.startOffset.offset === 'minus'
        ? add(startDate, { minutes: +this.schedule.startOffset.value })
        : sub(startDate, { minutes: +this.schedule.startOffset.value })
      : startDate;

    const endDate = TimeUtils.convertTimezone(
      new Date(this.schedule.nextRunEndTime),
      TimezoneUtils.mapUtcToKnownTimezone(this.building.timeZone)
    );

    this.endDateTime = this.schedule.endOffset?.offset
      ? this.schedule.endOffset.offset === 'minus'
        ? add(endDate, { minutes: +this.schedule.endOffset.value })
        : sub(endDate, { minutes: +this.schedule.endOffset.value })
      : endDate;
  }
}
